<template>
  <div>
    <div class="row">
      <div class="col-sm-2">
        <OverviewBox title="Toughening Batch" avatarImage="/glass/img/production-overview-batch2.png" :avatarCircleMode="false" :loading="pageDataLoading">
          <h4>Batch #{{batch.sequenceNumber}}</h4>
          
          <OverviewBoxList>
            <OverviewBoxListItem title="Created By">
              <LinkUser v-if="batch.user" :id="batch.user.id" :name="batch.user.name" />
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Station">
              <LinkProductionStation v-if="batch.sourceProductionStation" :id="batch.sourceProductionStation.id" :name="batch.sourceProductionStation.name" />
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Compliance">
              {{formatLookupName(batch.complianceType, ToughenBatchComplianceTypeLookup)}}
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Glass">
              {{formatLookupName(batch.glassType, ToughenBatchGlassTypeLookup)}}
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Luminous" v-if="isAutomotiveType">
              {{formatLookupName(batch.lumenTransType, ToughenBatchLumenTransTypeLookup)}}
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Panels" v-if="batch.productionPanels">
              {{batch.productionPanels.length}}
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Started">{{batch.startTime | formatDateTime}}</OverviewBoxListItem>
            <OverviewBoxListItem title="Finished">{{batch.endTime | formatDateTime}}</OverviewBoxListItem>
          </OverviewBoxList>
        </OverviewBox>

      </div>

      <div class="col-sm-10">
        <TabbedBox ref="tabBox" :loading="pageDataLoading">
          <template v-slot:tablist>
            <Tab :first="true" tabid="tab-batch-summary">
              <i class="far fa-clipboard-list-check"></i> Batch Details
            </Tab>
            <Tab tabid="tab-batch-documents">
              <i class="far fa-book"></i> Documents
            </Tab>
          </template>
          <template v-slot:tabcontent>
            <TabPage :first="true" tabid="tab-batch-summary">
              
              <DataPageArea title="Details">
                <template slot="actionArea">
                </template>
                <template slot="dataArea">
                  
                  <DataItem title="Created By" v-if="batch.user">
                    <LinkUser :id="batch.user.id" :name="batch.user.name" />
                  </DataItem>
                  <DataItem title="Station" v-if="batch.sourceProductionStation">
                    <LinkProductionStation :id="batch.sourceProductionStation.id" :name="batch.sourceProductionStation.name" />
                  </DataItem>
                  <DataItem title="Batch Number">#{{batch.sequenceNumber}}</DataItem>
                  <DataItem title="Batch Thickness">{{batch.batchThickness | formatDecimal}}mm</DataItem>
                  <DataItem title="Marking">{{capitalizeString(batch.markingType)}}</DataItem>
                  <DataItem title="Photo Taken">{{formatBoolean(batch.isPhotographTaken)}}</DataItem>
                  <DataItem title="Notes">{{batch.notes}}</DataItem>
                </template>
              </DataPageArea>

              <div class="row mt-3">
                <div class="col-lg-12">
                  <ContentTitle>Batch Data</ContentTitle>
                  <StaticDataTable class="static-table-list" smallRows>
                    <template v-slot:header>
                      <tr>
                        <th>Name</th>
                        <th>Value</th>
                        <th>Reading</th>
                      </tr>
                    </template>
                    <template v-slot:body>
                      <tr v-for="item in dataTableList" :key="item.name">
                        <td>{{item.name}}</td>
                        <td>{{item.value}}</td>
                        <td>{{formatHyphenEmptyValue(item.info)}}</td>
                      </tr>
                    </template>
                  </StaticDataTable>
                </div>
              </div>

              <div class="row mt-3">
                <div class="col-lg-12">
                  <h3>Batch Panels</h3>
                  <StaticDataTable class="static-table-list">
                    <template v-slot:header>
                      <tr>
                        <th>Panel</th>
                        <th>Size</th>
                        <th>Scan In Time</th>
                        <th>Order</th>
                        <th>Customer</th>
                      </tr>
                    </template>
                    <template v-slot:body>
                      <tr v-for="toughenPanel in batch.productionPanels" :key="toughenPanel.productionPanel.id">
                        <td>
                          <LinkProductionPanel :id="toughenPanel.productionPanel.id" :name="toughenPanel.productionPanel.barcode" />
                        </td>
                        <td>
                          {{toughenPanel.productionPanel.panelHeight}} x {{toughenPanel.productionPanel.panelWidth}}
                        </td>
                        <td>
                          {{toughenPanel.startTime | formatDateTime}}
                        </td>
                        <td>
                          <LinkOrder :id="toughenPanel.productionPanel.order.id" :name="`#${toughenPanel.productionPanel.order.sequenceNumber}`" />
                        </td>
                        <td>
                          <LinkContact :id="toughenPanel.productionPanel.order.contact.id" :name="toughenPanel.productionPanel.order.contact.name" />
                        </td>
                      </tr>
                    </template>
                  </StaticDataTable>
                </div>
              </div>
            </TabPage>

            <TabPage tabid="tab-batch-documents">
              <div class="row">
                <div class="col-lg-12">
                  <DocumentList
                    title="Documents"
                    :linkType="LinkTypes.tougheningBatch"
                    :linkId="batch.id"
                    :permissionResource="PermissionTypes.ProductionPermissionTypes.productionAdministrator"
                    class="mt-2" />
                </div>
              </div>
            </TabPage>
          </template>
        </TabbedBox>
      </div>
    </div>
  </div>
</template>

<script>

import DocumentList from '@/components/documentList.vue'
import { LinkTypes, ToughenBatchComplianceTypes, ToughenBatchGlassTypes, ToughenBatchComplianceTypeLookup, ToughenBatchGlassTypeLookup, ToughenBatchLumenTransTypeLookup } from '@/store/types'

export default {
  name: 'TougheningBatch',
  components: {
    DocumentList
  },
  data() {
    return {
      batch: {}
    }
  },
  computed: {
    isAutomotiveType() {
      return this.batch.complianceType == ToughenBatchComplianceTypes.automotive
    },
    isPatternGlassType() {
      return this.batch.glassType == ToughenBatchGlassTypes.pattern
    },
    dataTableList() {
      var dataList = [
        { name: 'Thickness (mm)', value: this.formatDecimal(this.batch.batchThickness) + 'mm', info: '' },
        { name: 'Batch V1', value: this.formatDecimal(this.batch.batchV1) + 'mm', info: '' },
        { name: 'Batch V2', value: this.formatDecimal(this.batch.batchV2) + 'mm', info: '' },
        { name: 'Batch V3', value: this.formatDecimal(this.batch.batchV3) + 'mm', info: '' },
        { name: 'Batch V4', value: this.formatDecimal(this.batch.batchV4) + 'mm', info: '' },
      ]
      if(this.isPatternGlassType) {
        dataList.push(
          { name: 'Batch V5', value: this.formatDecimal(this.batch.batchV5) + 'mm', info: '' },
          { name: 'Batch V6', value: this.formatDecimal(this.batch.batchV6) + 'mm', info: '' },
          { name: 'Batch V7', value: this.formatDecimal(this.batch.batchV7) + 'mm', info: '' },
          { name: 'Batch V8', value: this.formatDecimal(this.batch.batchV8) + 'mm', info: '' },
        )
      }

      dataList.push(
        { name: 'Particle Count', value: this.batch.batchParticleCount, info: '' },
        { name: 'Size Tolerance', value: this.formatBoolean(this.batch.sizeTolerance), info: this.batch.sizeToleranceReading },
        { name: 'Squareness', value: this.formatBoolean(this.batch.squareness), info: this.batch.squarenessReading },
        { name: 'Flatness', value: this.formatBoolean(this.batch.flatness), info: this.batch.flatnessReading },
        { name: 'Edge Kink', value: this.formatBoolean(this.batch.edgeKink), info: this.batch.edgeKinkReading },
        { name: 'Roller Wave', value: this.formatBoolean(this.batch.rollerWave), info: this.batch.rollerWaveReading },
        { name: 'Marking Type', value: this.capitalizeString(this.batch.markingType), info: '' },
        { name: 'Photo Taken', value: this.formatBoolean(this.batch.isPhotographTaken), info: '' },
      )

      return dataList
    }
  },
  created() {
    this.LinkTypes = LinkTypes
    this.ToughenBatchComplianceTypeLookup = ToughenBatchComplianceTypeLookup
    this.ToughenBatchGlassTypeLookup = ToughenBatchGlassTypeLookup
    this.ToughenBatchLumenTransTypeLookup = ToughenBatchLumenTransTypeLookup
  },
  mounted() {
    this.requestData()
  },
  methods: {
    requestData() {
      this.pageLoadData(this.$apiService.productionService.getTougheningBatch(this.$route.params.id))
      .then(response => {
        this.batch = response.data
      })
      .catch(err => this.handlePageLoadError(err))
    }
  }
}
</script>